/* Collector badges and rank. Earned state is derived from the live collection,
   so nothing here is persisted — it recomputes on every render.

   Deliberately not a leaderboard. Nothing here compares one collector to
   another: each section is a progression you advance through alone, and the
   rank is a single ladder driven by your own total badges. The top rank is
   withheld until every section is mastered, so it means breadth across all of
   them rather than depth in one. */

export const BRAND_BADGES = ["Nike", "Jordan", "Adidas", "New Balance", "ASICS", "Puma", "Converse", "Vans"];

export function totalWears(items) {
  return items.reduce((s, i) => s + i.wears, 0);
}

export function totalActions(items, action) {
  return items.reduce((s, i) => s + (i.history || []).filter((h) => h.action === action).length, 0);
}

export function brandsOwned(items) {
  return [...new Set(items.map((i) => i.brand))];
}

/* Each section carries exactly one `mastery` badge — the hardest one in it.
   Mastering every section is what unlocks the top rank, so the mastery flag is
   load-bearing rather than decorative: without it a section would count as
   mastered the moment its easiest badges happened to fill up. */
export const BADGE_SECTIONS = [
  {
    key: "brand_corps",
    title: "Brand Corps",
    icon: "🎖️",
    badges: [
      ...BRAND_BADGES.map((b) => ({
        key: `brand_${b}`,
        label: b,
        icon: "✓",
        test: (items) => brandsOwned(items).includes(b),
      })),
      { key: "brand_master", label: "Multi-Brand Master", icon: "🌐", mastery: true, test: (items) => brandsOwned(items).length >= 5 },
    ],
  },
  {
    key: "collectors_guard",
    title: "Collector's Guard",
    icon: "📘",
    badges: [
      { key: "first_item", label: "First Entry", icon: "🎫", test: (items) => items.length >= 1 },
      { key: "five_items", label: "5 Catalogued", icon: "📇", test: (items) => items.length >= 5 },
      { key: "ten_items", label: "10 Catalogued", icon: "🗂️", test: (items) => items.length >= 10 },
      { key: "multi_category", label: "Multi-Category Collector", icon: "🧭", test: (items) => new Set(items.map((i) => i.category)).size >= 2 },
      { key: "deadstock_keeper", label: "Deadstock Keeper", icon: "🧊", test: (items) => items.some((i) => i.status === "Deadstock") },
      { key: "full_passport", label: "Passport Full", icon: "📘", mastery: true, test: (items) => items.length >= 25 },
    ],
  },
  {
    key: "preservation_corps",
    title: "Preservation Corps",
    icon: "🧼",
    badges: [
      { key: "first_clean", label: "First Clean", icon: "🧼", test: (items) => totalActions(items, "Cleaned") >= 1 },
      { key: "rotation_master", label: "Rotation Master", icon: "🔄", test: (items) => totalActions(items, "Rotated") >= 5 },
      { key: "pristine_keeper", label: "Pristine Keeper", icon: "✨", test: (items) => items.some((i) => i.condition >= 95) },
      { key: "no_neglect", label: "No Item Left Behind", icon: "🛡️", test: (items) => items.length >= 3 && items.every((i) => i.condition >= 70) },
      { key: "preservation_pro", label: "Preservation Pro", icon: "🧽", mastery: true, test: (items) => totalActions(items, "Cleaned") >= 10 },
    ],
  },
  {
    key: "flight_squadron",
    title: "Flight Squadron",
    icon: "✈️",
    badges: [
      { key: "first_flight", label: "First Flight", icon: "🛫", test: (items) => totalWears(items) >= 1 },
      { key: "frequent_flyer", label: "Frequent Flyer", icon: "🎟️", test: (items) => totalWears(items) >= 25 },
      { key: "logbook_kept", label: "Logbook Kept", icon: "📖", test: (items) => items.filter((i) => i.wears >= 1).length >= 3 },
      { key: "century_club", label: "Century Club", icon: "💯", mastery: true, test: (items) => totalWears(items) >= 100 },
    ],
  },
  {
    key: "intelligence_division",
    title: "Intelligence Division",
    icon: "🔍",
    badges: [
      { key: "first_scan", label: "First Scan", icon: "📷", test: (items) => totalActions(items, "AI Scan") >= 1 },
      { key: "well_documented", label: "Well Documented", icon: "🗃️", test: (items) => items.filter((i) => (i.photos || []).length >= 1).length >= 3 },
      { key: "field_analyst", label: "Field Analyst", icon: "🕵️", mastery: true, test: (items) => totalActions(items, "AI Scan") >= 10 },
    ],
  },
];

export const RANKS = [
  { min: 0, label: "Horizon" },
  { min: 3, label: "Riser" },
  { min: 6, label: "Voyager" },
  { min: 9, label: "Vanguard" },
  { min: 13, label: "Luminary" },
  { min: 17, label: "Elite" },
  { min: 21, label: "Ascendant" },
  { min: 25, label: "Paragon" },
];

export const TOP_RANK_LABEL = "Guru";
export const TOP_RANK_SUBTITLE = "Real mastery across every section, not just total badges";

/** Every section's progress, plus the single rank it all rolls up into. */
export function computeBadgeProgress(items) {
  const sections = BADGE_SECTIONS.map((section) => {
    const earnedKeys = new Set(section.badges.filter((b) => b.test(items)).map((b) => b.key));
    const masteryBadge = section.badges.find((b) => b.mastery);
    /* A section counts as mastered when its mastery badge is earned — not when
       its badge count happens to fill up, which would let the easy ones stand in
       for the hard one. */
    const mastered = masteryBadge ? earnedKeys.has(masteryBadge.key) : earnedKeys.size === section.badges.length;
    return { ...section, earnedKeys, earnedCount: earnedKeys.size, totalBadges: section.badges.length, mastered };
  });

  const totalEarned = sections.reduce((s, sec) => s + sec.earnedCount, 0);
  const totalBadges = sections.reduce((s, sec) => s + sec.totalBadges, 0);
  const allSectionsMastered = sections.every((s) => s.mastered);

  let rank = RANKS[0].label;
  for (const r of RANKS) if (totalEarned >= r.min) rank = r.label;
  if (allSectionsMastered) rank = TOP_RANK_LABEL;

  return { sections, totalEarned, totalBadges, allSectionsMastered, rank };
}
